'use client';

import React from 'react';
import { Select as AntSelect, ConfigProvider } from 'antd';
import type { SelectProps as AntSelectProps } from 'antd';
import { orionTheme } from '../theme/orion-theme';
import clsx from 'clsx';

export interface SelectProps<ValueType = any> extends AntSelectProps<ValueType> {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Select component - Ant Design Select with OpenStore Orion theming
 * 
 * This component provides FULL access to all Ant Design Select props while
 * automatically applying the OpenStore Orion theme.
 * 
 * Features:
 * - Orion design specifications (colors, typography, spacing)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Select features and props
 * 
 * @example
 * ```tsx
 * // Basic select with Orion theming
 * <Select placeholder="Select an option">
 *   <Select.Option value="option1">Option 1</Select.Option>
 *   <Select.Option value="option2">Option 2</Select.Option>
 * </Select>
 * 
 * // All Ant Design Select props work
 * <Select
 *   mode="multiple"              // ✅ Multiple selection
 *   allowClear                   // ✅ Clear button
 *   showSearch                   // ✅ Searchable
 *   placeholder="Search and select..."
 *   optionFilterProp="children"  // ✅ Search filter
 *   filterOption={(input, option) =>
 *     (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
 *   }
 *   options={[
 *     { value: 'electronics', label: 'Electronics' },
 *     { value: 'clothing', label: 'Clothing' },
 *     { value: 'sports', label: 'Sports' },
 *   ]}
 *   loading={isLoading}          // ✅ Loading state
 *   disabled={isDisabled}        // ✅ Disabled state
 *   size="large"                 // ✅ Sizing
 *   status="error"               // ✅ Validation status
 *   onChange={handleChange}      // ✅ Change handler
 *   onSearch={handleSearch}      // ✅ Search handler
 *   onSelect={handleSelect}      // ✅ Select handler
 *   onDeselect={handleDeselect}  // ✅ Deselect handler
 *   onClear={handleClear}        // ✅ Clear handler
 *   onFocus={handleFocus}        // ✅ Focus handler
 *   onBlur={handleBlur}          // ✅ Blur handler
 *   onDropdownVisibleChange={handleDropdownChange} // ✅ Dropdown events
 * />
 * 
 * // Searchable select with custom filter
 * <Select
 *   showSearch
 *   placeholder="Search products..."
 *   optionFilterProp="children"
 *   filterOption={(input, option) =>
 *     (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
 *   }
 *   filterSort={(optionA, optionB) =>
 *     (optionA?.children ?? '').toLowerCase().localeCompare(
 *       (optionB?.children ?? '').toLowerCase()
 *     )
 *   }
 * >
 *   <Select.Option value="product1">Wireless Headphones</Select.Option>
 *   <Select.Option value="product2">Smart Watch</Select.Option>
 *   <Select.Option value="product3">Running Shoes</Select.Option>
 * </Select>
 * 
 * // Multiple selection with tags
 * <Select
 *   mode="tags"
 *   style={{ width: '100%' }}
 *   placeholder="Add tags..."
 *   tokenSeparators={[',']}
 *   options={tagOptions}
 *   maxTagCount="responsive"
 *   maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
 * />
 * 
 * // With custom dropdown render
 * <Select
 *   placeholder="Custom dropdown"
 *   dropdownRender={(menu) => (
 *     <div>
 *       {menu}
 *       <div style={{ padding: '8px 0' }}>
 *         <Button type="text" icon={<PlusOutlined />} block>
 *           Add new item
 *         </Button>
 *       </div>
 *     </div>
 *   )}
 * >
 *   <Select.Option value="option1">Option 1</Select.Option>
 *   <Select.Option value="option2">Option 2</Select.Option>
 * </Select>
 * 
 * // With Tailwind overrides
 * <Select 
 *   placeholder="Custom styled select"
 *   className="shadow-lg border-orion-200 rounded-xl"
 *   dropdownClassName="shadow-2xl border-orion-100"
 * >
 *   <Select.Option value="option1">Option 1</Select.Option>
 * </Select>
 * 
 * // Async search with remote data
 * <Select
 *   showSearch
 *   placeholder="Search users..."
 *   defaultActiveFirstOption={false}
 *   suffixIcon={null}
 *   filterOption={false}
 *   onSearch={handleAsyncSearch}
 *   loading={searchLoading}
 *   notFoundContent={searchLoading ? <Spin size="small" /> : 'No data'}
 *   options={searchResults}
 * />
 * ```
 */
export const Select: React.FC<SelectProps> = (props) => {
  const {
    className,
    ...restProps // All other Ant Design props pass through
  } = props;

  return (
    <ConfigProvider theme={orionTheme}>
      <AntSelect
        className={clsx(
          'orion-select',
          // Add custom classes for Tailwind overrides
          className
        )}
        {...restProps} // Pass through ALL Ant Design props
      />
    </ConfigProvider>
  );
};

Select.displayName = 'Select';

// Export all Select sub-components from Ant Design
export const { 
  Option: SelectOption,
  OptGroup: SelectOptGroup 
} = AntSelect;

// Export convenience components for common patterns
export const SearchableSelect: React.FC<SelectProps> = (props) => (
  <Select
    showSearch
    optionFilterProp="children"
    filterOption={(input, option) =>
      (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
    }
    {...props}
  />
);

export const MultiSelect: React.FC<SelectProps> = (props) => (
  <Select
    mode="multiple"
    allowClear
    maxTagCount="responsive"
    {...props}
  />
);

export const TagSelect: React.FC<SelectProps> = (props) => (
  <Select
    mode="tags"
    tokenSeparators={[',']}
    maxTagCount="responsive"
    {...props}
  />
);

export const AsyncSelect: React.FC<SelectProps & {
  onSearch?: (value: string) => void;
  loading?: boolean;
}> = (props) => {
  const { onSearch, loading, ...selectProps } = props;
  
  return (
    <Select
      showSearch
      defaultActiveFirstOption={false}
      suffixIcon={null}
      filterOption={false}
      onSearch={onSearch}
      loading={loading}
      notFoundContent={loading ? <span>Loading...</span> : 'No data'}
      {...selectProps}
    />
  );
};